//#region    ////////// packages
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useState } from 'react';
//#endregion ////////// packages

//#region    ////////// components
import FloatingLabelInput from '@/shared/components/inputs/FloatingLabelInput';
import AuthLayout from '@/pages/auth/_components/AuthLayout';
//#endregion ////////// components

//#region    ////////// utils & hooks
import IconCircleInfo from '@/shared/assets/svg/common/icon-circle-info.svg?react';
import IconCircleCheck from '@/shared/assets/svg/common/icon-circle-check.svg?react';
import CustomButton from '@/shared/shadcn/custom/CustomButton';
import IconChevronLeft from '@/shared/assets/svg/common/icon-chevron-left.svg?react';
import { getAuth } from '@/lib/api/generated/auth/auth';

const { authRequestPasswordReset } = getAuth();

export default function SigninPage() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      RHF_resetEmail: '',
      RHF_resetLoginId: '',
    },
  });
  const [parentRef] = useAutoAnimate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 피드백 배너 설정 State
  const [bannerInfo, setBannerInfo] = useState<{ type: 'error' | 'success'; text?: string; children?: React.ReactNode; isShow: boolean } | null>(null);

  const onSubmit = async (data: { RHF_resetEmail: string; RHF_resetLoginId: string }) => {
    setIsSubmitting(true);
    try {
      await authRequestPasswordReset({ loginId: data.RHF_resetLoginId, email: data.RHF_resetEmail });
    } catch {
      // 서버는 항상 200을 반환하므로 에러 시에도 동일 안내 표시
    } finally {
      setIsSubmitting(false);
    }

    setBannerInfo({
      type: 'success',
      isShow: true,
      children: (
        <p className='text-sm'>
          <span className='font-semibold'>입력하신 이메일이 등록되어 있다면 비밀번호 재설정 메일이 발송되었습니다.</span>
          <br />
          <span className='font-medium'>메일함을 확인하여 비밀번호를 재설정해주세요.</span>
        </p>
      ),
    });
  };

  return (
    <AuthLayout>
      <div>
        <div className='relative'>
          <CustomButton className='no-hover bg-basic-white group absolute top-2 -left-4 -translate-x-full rounded-lg' onClick={() => navigate(-1)}>
            <IconChevronLeft className='h-8 w-8 text-gray-600 group-hover:text-gray-800' />
          </CustomButton>

          <h3 className='text-basic-black text-d3xl font-semibold whitespace-pre-line'>Reset password</h3>
        </div>
        <p className='mt-1 text-sm text-gray-900'>
          Enter your email to reset password.
          <br />
          Password reset email will be on the way.
        </p>

        <div className='mt-8' ref={parentRef}>
          {bannerInfo && (
            <FeedbackBanner type={bannerInfo.type} text={bannerInfo.text}>
              {bannerInfo.children}
            </FeedbackBanner>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className='mt-3 flex flex-col gap-5'>
            <div className='flex flex-col gap-4'>
              <FloatingLabelInput
                {...register('RHF_resetEmail', { required: 'email is required.' })}
                error={errors.RHF_resetEmail?.message}
                label='Email'
                required
              />
              <FloatingLabelInput
                {...register('RHF_resetLoginId', { required: 'login id is required.' })}
                error={errors.RHF_resetLoginId?.message}
                label='Login ID'
                required
              />
            </div>
            <button
              type='submit'
              disabled={isSubmitting}
              className='text-basic-white h-14 w-full rounded-lg bg-blue-400 text-base font-semibold disabled:opacity-60'>
              {isSubmitting ? 'Sending...' : 'Send email'}
            </button>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
}

interface FeedbackBannerProps {
  type: 'success' | 'error'; // 피드백 타입
  text?: string; // 피드백 메시지
  children?: React.ReactNode; // text 보다 더 정교한 피드백을 위한 컴포넌트
}

const bannerConfig = {
  success: {
    icon: (
      <IconCircleCheck
        style={
          {
            '--icon-bg': 'var(--support-green)',
            '--icon-fg': 'var(--color-basic-white)',
          } as React.CSSProperties
        }
        className='h-5 w-5'
      />
    ),
    bg: 'bg-support-green-shade text-support-green',
  },
  error: {
    icon: (
      <IconCircleInfo
        style={
          {
            '--icon-bg': 'var(--support-red)',
            '--icon-fg': 'var(--color-basic-white)',
          } as React.CSSProperties
        }
        className='h-5 w-5'
      />
    ),
    bg: 'bg-support-red-shade text-support-red',
  },
};

/** 유저에게 피드백을 주는 컴포넌트 */
function FeedbackBanner({ type, text, children }: FeedbackBannerProps) {
  return (
    <div className={`mt-8 flex items-center gap-3 rounded-lg p-3 text-sm ${bannerConfig[type]?.bg ?? ''}`}>
      {bannerConfig[type]?.icon}
      <div>{text || children}</div>
    </div>
  );
}
